import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Tag, ArrowRight } from "lucide-react";
import axios from "axios";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppButton } from "@/components/WhatsAppButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categoryColors = {
  Tecnología: "bg-blue-100 text-blue-700",
  Comparativas: "bg-purple-100 text-purple-700",
  Instalaciones: "bg-green-100 text-green-700",
  Servicios: "bg-accent/10 text-accent",
};

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/blog`)
      .then((r) => setPosts(r.data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="blog-page" className="min-h-screen bg-[#F5F5F5]">
      <Navbar />

      {/* Hero */}
      <div
        className="pt-32 pb-20 text-center relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #003366 0%, #1A1A2E 100%)" }}
      >
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: "radial-gradient(circle at 70% 50%, #00AEEF 0%, transparent 50%)" }} />
        <div className="relative max-w-3xl mx-auto px-6">
          <Link
            to="/"
            data-testid="blog-back-home"
            className="inline-flex items-center gap-2 text-white/60 hover:text-accent text-sm font-body mb-6 transition-colors duration-200"
          >
            <ArrowLeft size={16} /> Inicio
          </Link>
          <h1 className="font-heading font-black text-white text-5xl md:text-6xl mb-4">
            Blog <span className="text-accent">Ms Vision</span>
          </h1>
          <p className="font-body text-white/65 text-lg">
            Noticias, tecnología y tendencias en telecomunicaciones
          </p>
        </div>
      </div>

      {/* Posts grid */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden shadow">
                <div className="shimmer h-52" />
                <div className="p-6 space-y-3">
                  <div className="shimmer h-4 rounded w-1/3" />
                  <div className="shimmer h-6 rounded w-full" />
                  <div className="shimmer h-4 rounded w-3/4" />
                </div>
              </div>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20">
            <p className="font-body text-gray-500 text-lg">No hay posts disponibles aún.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post, i) => (
              <motion.div
                key={post.id || post.slug}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                data-testid={`blog-post-card-${post.slug}`}
                className="group bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.08)] hover:-translate-y-2 hover:shadow-[0_20px_40px_-4px_rgba(0,174,239,0.15)] hover:border-accent/20 transition-all duration-300"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={post.image_url}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.parentElement.style.background = "linear-gradient(135deg, #003366, #00AEEF)";
                    }}
                  />
                  <span className={`absolute top-4 left-4 text-xs font-heading font-bold px-3 py-1.5 rounded-full ${categoryColors[post.category] || "bg-gray-100 text-gray-600"}`}>
                    {post.category}
                  </span>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 text-gray-400 text-xs font-body mb-3">
                    <Calendar size={12} />
                    <span>{post.published_at ? new Date(post.published_at).toLocaleDateString("es-AR", { day: "numeric", month: "long", year: "numeric" }) : ""}</span>
                  </div>
                  <h3 className="font-heading font-bold text-primary text-lg leading-snug mb-3 line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="font-body text-gray-500 text-sm leading-relaxed mb-5 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <Link
                    to={`/blog/${post.slug}`}
                    data-testid={`blog-read-more-${post.slug}`}
                    className="flex items-center gap-2 text-accent font-heading font-semibold text-sm hover:gap-3 transition-all duration-200"
                  >
                    Leer más <ArrowRight size={14} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}
